import { type ReactElement, type ReactNode, type Ref } from 'react'
import {
  CardSubtitle,
  CardItemContent,
  CardList,
  CardListItem,
  CardTitle,
} from '@/components/Card'
import { CircularProgressbar } from '@/components/CircularProgressbar'
import {
  AutoSkeleton,
  IsSkeletonContext,
  useIsSkeleton,
} from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { Spinner } from '@/components/Spinner'
import styles from './DocumentCards.module.css'
import { IconButton } from '@/components/IconButton'
import { DocumentThumbnail } from '@/features/docs/components/DocumentThumbnail'

interface DocumentCardListProps {
  children: ReactNode
  'aria-labelledby'?: string
  'aria-label'?: string
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * A list of {@link DocumentCardItem}s.
 */
export function DocumentCardList({ children, ...rest }: DocumentCardListProps) {
  return <CardList {...rest}>{children}</CardList>
}

export interface DocumentCardItemProps {
  /**
   * Whether the item is disabled.
   */
  isDisabled?: boolean
  /**
   * Document title.
   */
  title?: string
  /**
   * Document description. Could be the type, for example.
   */
  description?: string
  /**
   * Thumbnail to render for the document, if any. Otherwise, a plain file
   * icon will be rendered.
   */
  thumbnail?: ReactElement
  /**
   * If specified, an upload progress indicator will be rendered instead
   * of the thumbnail. When a number, should be between 0 and 100. When
   * `indefinite`, a regular spinner will be rendered
   */
  progress?: number | 'indefinite'
  /**
   * Extra stuff to render on the side, for example an icon button
   * or an error indicator.
   */
  extras?: ReactNode
  /**
   * Whether we're rendering as a skeleton.
   */
  isSkeleton?: boolean
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the document title.
   */
  titleTestId?: string
  /**
   * Test ID attribute for the document description.
   */
  descriptionTestId?: string
}

/**
 * A card representing a document.
 */
export function DocumentCardItem({
  isDisabled,
  title,
  description,
  thumbnail,
  extras,
  progress,
  isSkeleton,
  testId,
  titleTestId,
  descriptionTestId,
}: DocumentCardItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const { vary, ref, index } = useSkeletonListContext()

  const icon = thumbnail && <div className={styles.icon}>{thumbnail}</div>

  if (typeof progress === 'number') {
    extras = (
      <div className={styles.status}>
        <CircularProgressbar size={24} thickness={3} progress={progress} />
      </div>
    )
  }

  if (progress === 'indefinite') {
    extras = (
      <div className={styles.status}>
        <Spinner size={24} thickness={3} />
      </div>
    )
  }

  return (
    <CardListItem
      isDisabled={isDisabled}
      textValue={title}
      ref={ref as Ref<HTMLDivElement>}
      testId={testId}
    >
      <IsSkeletonContext value={isSkeleton}>
        <CardItemContent spacingY="base" icon={icon} extras={extras}>
          <CardTitle overflowWrap="break-all">
            <AutoSkeleton characters={vary((index + 1) % 3 === 0 ? 16 : 28)}>
              <span data-test={titleTestId}>{title}</span>
            </AutoSkeleton>
          </CardTitle>
          {(description || isSkeleton) && (
            <CardSubtitle variant="sm-description">
              <AutoSkeleton characters={vary((index + 1) % 3 === 0 ? 36 : 24)}>
                <span data-test={descriptionTestId}>{description}</span>
              </AutoSkeleton>
            </CardSubtitle>
          )}
        </CardItemContent>
      </IsSkeletonContext>
    </CardListItem>
  )
}

/**
 * A pre-filled skeleton for a {@link DocumentCardItem}.
 */
export function DocumentCardItemSkeleton() {
  return (
    <DocumentCardItem
      isSkeleton
      title="Placeholder"
      thumbnail={<DocumentThumbnail isSkeleton />}
      extras={<IconButton name="more" label="Placeholder" />}
    />
  )
}
